import React from "react";
import Fuse from "fuse.js";
import styles from "../styles/components/SearchText.css";

const SEARCH_MIN_CHAR_LENGTH = 3;

export function search(keys, items, query) {
    const fuse = new Fuse(items, {
        shouldSort: true,
        threshold: 0.3,
        keys: keys,
        minMatchCharLength: 3,
        // Fuse scores matches based in part on how close they are to where you expect to find them. We specify
        // a location of 0 (arbitrary) and a large distance so that all matches will appear to be pretty close
        // to their expected location
        location: 0,
        distance: 1000000,
        includeMatches: true
    });
    const fuseSearchPoints = fuse.search(query);

    // for query highlighting, attach each point's 'matches' data
    return fuseSearchPoints.map(point => {
        const { item, matches } = point;
        return {
            ...item,
            matchIndices: matches.length > 0 ? matches[0].indices : []
        };
    });
}

export default function SearchText({ text, matchIndices }) {
    matchIndices = matchIndices || [];

    if (matchIndices.length === 0 || text === "") {
        return <span>{text}</span>;
    }

    // Fuse finds multiple 'matches' per string and calculates a score. If a match's score is within the threshold that
    // object will get returned when calling '.search'. However, when returning the match indices, all of the matches
    // are returned, not just the ones with the good score. Therefore, highlighting low score matches can look like a
    // hot mess.  We can eliminate some bad string matches by highlighting only those with the max length. (e.g) Query:
    // "University" Without Reduction: beca*use* it po*ints* out the *University* and degree is nationally accredited
    // With Reduction:    because it points out the *University* and degree is nationally accredited
    const matchLengths = matchIndices.map(([start, end]) => end - start);
    const maxLength = Math.max(0, ...matchLengths);

    const onlyDisplayLongestMatches =
        matchIndices.length > 1 && maxLength > SEARCH_MIN_CHAR_LENGTH;
    const indices = onlyDisplayLongestMatches
        ? matchIndices.filter(([start, end]) => end - start === maxLength)
        : matchIndices;

    let lastIndex = 0;
    const elements = [];
    for (let [start, end] of indices) {
        // add the text between the end of the previous match and the start of the current one
        const startString = text.substring(lastIndex, start);
        if (startString.length > 0) {
            elements.push(startString);
        }

        // add the matching text in a <mark> tag
        lastIndex = end + 1;
        const highlightString = text.substring(start, lastIndex);
        if (highlightString.length > 0) {
            elements.push(
                <mark
                    key={`match(${start}, ${end})`}
                    className={styles.highlightedText}
                >
                    {highlightString}
                </mark>
            );
        }
    }

    const lastString = text.substring(lastIndex);
    if (lastString.length > 0) {
        elements.push(lastString);
    }
    return <span>{elements}</span>;
}



// WEBPACK FOOTER //
// ./src/components/SearchText.js